import { Eye, Info, RefreshCw, Save, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { NumberSequenceCodeFields } from "./number-sequence-code-fields";
import { NumberSequenceModuleGrid } from "./number-sequence-module-grid";
import { NumberSequenceResetDialog } from "./number-sequence-reset-dialog";
import { useNumberSequence } from "./use-number-sequence";

import type { NumberSequenceFormValues } from "./number-sequence-schema";

interface NumberSequenceCardProps {
  workspaceId: string;
  canUpdate?: boolean;
}

export function NumberSequenceCard({
  workspaceId,
  canUpdate = true,
}: NumberSequenceCardProps) {
  const {
    form,
    selectedModuleCode,
    setSelectedModuleCode,
    activeModuleInfo,
    moduleSummaries,
    registeredModules,
    placeholders,
    presets,
    previewCode,
    isSaving,
    isResetDialogOpen,
    openResetDialog,
    closeResetDialog,
    handleResetCounter,
    handleResetToDefault,
    insertPlaceholder,
    applyPreset,
    onSubmit,
  } = useNumberSequence(workspaceId);

  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = form;

  const currentAutoGenerate = watch("autoGenerateOnCreate");
  const currentResetPeriod = watch("resetPeriod");

  return (
    <div className="space-y-6">
      {/* Overview Cards per Module */}
      <NumberSequenceModuleGrid
        registeredModules={registeredModules}
        selectedModuleCode={selectedModuleCode}
        setSelectedModuleCode={setSelectedModuleCode}
        moduleSummaries={moduleSummaries}
      />

      {/* Editor Card for Selected Module */}
      <Card className="border border-border/80 shadow-sm">
        <CardHeader className="pb-4 border-b border-border/50">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <CardTitle className="text-lg">
                  {activeModuleInfo?.name ?? "Pengaturan Penomoran"}
                </CardTitle>
                <span className="font-mono text-xs font-semibold px-2.5 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                  {activeModuleInfo?.badge ?? selectedModuleCode}
                </span>
              </div>
              <CardDescription className="text-xs text-muted-foreground mt-1">
                {activeModuleInfo?.description} (Kode Sistem:{" "}
                <code className="text-[11px] font-mono font-medium">
                  {selectedModuleCode}
                </code>
                )
              </CardDescription>
            </div>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleResetToDefault}
              className="text-xs self-start sm:self-auto gap-1.5"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Kembalikan ke Default
            </Button>
          </div>
        </CardHeader>

        <CardContent className="pt-6">
          <form onSubmit={onSubmit} className="space-y-6">
            {/* Live Preview Banner */}
            <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 sm:p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-primary" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                    Simulasi Preview Nomor Berikutnya
                  </span>
                </div>
                <span className="text-[11px] text-muted-foreground">
                  Format Penomoran
                </span>
              </div>
              <div className="flex items-baseline gap-3 flex-wrap">
                <div className="font-mono text-xl sm:text-2xl font-extrabold tracking-tight text-foreground bg-background/80 px-3 py-1.5 rounded-lg border border-border/80 shadow-xs">
                  {previewCode}
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground mt-2 flex items-center gap-1.5">
                <Info className="w-4 h-4 text-primary shrink-0" />
                Dihasilkan dari gabungan:{" "}
                <span className="font-mono text-foreground font-semibold">
                  Prefix + [Nomor Urut Padded] + Suffix
                </span>
              </p>
            </div>

            {/* Presets */}
            <div>
              <Label className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-primary" />
                Preset Format Cepat
              </Label>
              <div className="flex flex-wrap gap-2">
                {presets.map((p, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => applyPreset(p.prefix, p.suffix)}
                    className="text-xs px-2.5 py-1 rounded-md border border-border bg-card hover:bg-muted font-mono text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Document Name */}
              <div className="space-y-1.5">
                <Label htmlFor="docName" className="text-xs font-medium">
                  Nama Dokumen / Transaksi
                </Label>
                <Input
                  id="docName"
                  {...register("name")}
                  className="font-medium text-sm"
                  placeholder="e.g. Penjualan Toko / Kasir"
                />
                {errors.name && (
                  <p className="text-xs text-destructive">
                    {errors.name.message}
                  </p>
                )}
              </div>

              {/* Reset Period */}
              <div className="space-y-1.5">
                <Label htmlFor="resetPeriod" className="text-xs font-medium">
                  Siklus Reset Nomor Urut
                </Label>
                <Select
                  value={currentResetPeriod}
                  onValueChange={(val) =>
                    setValue(
                      "resetPeriod",
                      val as NumberSequenceFormValues["resetPeriod"],
                      { shouldDirty: true },
                    )
                  }
                >
                  <SelectTrigger id="resetPeriod" className="text-sm">
                    <SelectValue placeholder="Pilih siklus reset" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">
                      Harian (Setiap Hari Mulai dari 1)
                    </SelectItem>
                    <SelectItem value="monthly">
                      Bulanan (Setiap Bulan Mulai dari 1)
                    </SelectItem>
                    <SelectItem value="yearly">
                      Tahunan (Setiap Tahun Mulai dari 1)
                    </SelectItem>
                    <SelectItem value="never">
                      Tidak Pernah (Lanjut Terus)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <NumberSequenceCodeFields
              register={register}
              errors={errors}
              placeholders={placeholders}
              insertPlaceholder={insertPlaceholder}
            />

            {/* Numeric Settings: Size (Digit Padding), Step (Increment), Next (Counter) */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 pt-2">
              <div className="space-y-1.5">
                <Label htmlFor="sizeInput" className="text-xs font-medium">
                  Panjang Digit (Padding Size)
                </Label>
                <Input
                  id="sizeInput"
                  type="number"
                  min={1}
                  max={10}
                  {...register("size", { valueAsNumber: true })}
                  className="font-mono text-sm"
                />
                <p className="text-[11px] text-muted-foreground">
                  Misal: 4 ➔ <code className="font-mono font-bold">0001</code>,
                  5 ➔ <code className="font-mono font-bold">00001</code>
                </p>
                {errors.size && (
                  <p className="text-xs text-destructive">
                    {errors.size.message}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="stepInput" className="text-xs font-medium">
                  Kenaikan Urut (Step)
                </Label>
                <Input
                  id="stepInput"
                  type="number"
                  min={1}
                  {...register("step", { valueAsNumber: true })}
                  className="font-mono text-sm"
                />
                <p className="text-[11px] text-muted-foreground">
                  Kelipatan kenaikan per transaksi (default: 1)
                </p>
                {errors.step && (
                  <p className="text-xs text-destructive">
                    {errors.step.message}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="nextInput" className="text-xs font-medium">
                    Nomor Berikutnya (Next)
                  </Label>
                  <button
                    type="button"
                    onClick={openResetDialog}
                    className="text-[11px] text-destructive hover:underline font-medium"
                  >
                    Reset ke 1
                  </button>
                </div>
                <Input
                  id="nextInput"
                  type="number"
                  min={1}
                  {...register("next", { valueAsNumber: true })}
                  className="font-mono text-sm"
                />
                <p className="text-[11px] text-muted-foreground">
                  Counter urut saat ini di database
                </p>
                {errors.next && (
                  <p className="text-xs text-destructive">
                    {errors.next.message}
                  </p>
                )}
              </div>
            </div>

            {/* Toggle Auto Generate */}
            <div className="flex items-center justify-between p-4 rounded-xl border border-border bg-card">
              <div className="space-y-0.5">
                <Label
                  htmlFor="autoGenSwitch"
                  className="text-xs font-medium cursor-pointer"
                >
                  Generate Nomor Otomatis Saat Transaksi Dibuat
                </Label>
                <p className="text-[11px] text-muted-foreground">
                  Jika aktif, transaksi yang tidak diisi kodenya akan otomatis
                  diberikan nomor urut ini.
                </p>
              </div>
              <Checkbox
                id="autoGenSwitch"
                checked={currentAutoGenerate}
                onCheckedChange={(val) =>
                  setValue("autoGenerateOnCreate", val === true, {
                    shouldDirty: true,
                  })
                }
              />
            </div>

            {/* Submit Action */}
            <div className="flex items-center justify-end gap-3 pt-2">
              <Button
                type="submit"
                disabled={isSaving || !canUpdate}
                className="gap-2"
              >
                {isSaving ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Menyimpan...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Simpan Pengaturan
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Confirmation Dialog for Resetting Counter */}
      <NumberSequenceResetDialog
        isOpen={isResetDialogOpen}
        onOpenChange={closeResetDialog}
        moduleName={activeModuleInfo?.name}
        onConfirm={handleResetCounter}
      />
    </div>
  );
}
